/**
 * How the app talks about where an offered visit is (security plan PR-18,
 * revised by the owner on 2026-10-06).
 *
 * The street address arrives only once the visit is ASSIGNED to her (a pending
 * request no longer unlocks it), and never while she is an applicant. Before
 * that the API sends one of:
 *
 *   - "cross_street": the patient's nearest cross street plus the area
 *     ("W 125th St & Frederick Douglass Blvd, Manhattan, NY 10027"), with the
 *     pin at the intersection. She may get directions to the corner.
 *   - "approximate": only the area ("Flushing, NY 11355") and a pin rounded to
 *     3 decimals (at most about 70 m from the patient). Used when no cross
 *     street is stored for the patient.
 *
 * These helpers keep every screen saying that the same way, and never print an
 * empty line or "undefined" when a field is missing.
 */
import type { LocationPrecision } from "@/lib/api";

/** The location fields every offered-visit payload carries. */
export interface VisitLocationFields {
    address: string | null;
    area?: string | null;
    /** "A & B" when `locationPrecision` is "cross_street". */
    crossStreet?: string | null;
    locationPrecision?: LocationPrecision;
}

/** Shown when nothing at all is known about where the patient is. */
export const AREA_UNKNOWN_TEXT = "Address shared once the visit is yours";

/** One-line hint under a location that is not the street address. */
export const EXACT_ADDRESS_ONCE_YOURS_TEXT = "Exact address shown once the visit is yours";

/**
 * True when the server sent only the area and a rounded pin. An API from
 * before PR-18 sends no `locationPrecision`, and it always sent the full
 * address.
 */
export function isApproximateLocation(visit: VisitLocationFields): boolean {
    return visit.locationPrecision === "approximate";
}

/** True when the server sent the cross street, pinned at the intersection. */
export function isCrossStreetLocation(visit: VisitLocationFields): boolean {
    return visit.locationPrecision === "cross_street";
}

/** True when the street address was withheld (cross street or area). */
export function withholdsExactAddress(visit: VisitLocationFields): boolean {
    return isApproximateLocation(visit) || isCrossStreetLocation(visit);
}

/**
 * The single line to print for a visit's location: the street address when
 * she has it, the cross street and area before that, otherwise the area.
 * Never empty.
 */
export function visitLocationLine(visit: VisitLocationFields): string {
    const address = visit.address?.trim() ?? "";
    const area = visit.area?.trim() ?? "";
    if (isApproximateLocation(visit)) {
        return area || address || AREA_UNKNOWN_TEXT;
    }
    if (isCrossStreetLocation(visit)) {
        // The server already sends "<cross street>, <area>" in `address`;
        // rebuild it only if a field is missing.
        const cross = visit.crossStreet?.trim() ?? "";
        const rebuilt = [cross, area].filter((x) => x !== "").join(", ");
        return address || rebuilt || AREA_UNKNOWN_TEXT;
    }
    return address || area || AREA_UNKNOWN_TEXT;
}

/** The label above the location: what kind of place the line is. */
export function locationChipLabel(visit: VisitLocationFields): string {
    if (isCrossStreetLocation(visit)) {
        return "CROSS STREETS";
    }
    return isApproximateLocation(visit) ? "AREA" : "ADDRESS";
}

/**
 * Show the "exact address once the visit is yours" hint? Under a cross street
 * or a real area; not when nothing is known, since the line already says the
 * address comes later.
 */
export function showsExactAddressHint(visit: VisitLocationFields): boolean {
    return withholdsExactAddress(visit) && visitLocationLine(visit) !== AREA_UNKNOWN_TEXT;
}

/**
 * Can "Get directions" be offered? To the street address, or to the
 * intersection of a cross street. Never to a rounded pin: that would route her
 * to a door up to ~70 m from the patient's.
 */
export function allowsDirections(visit: VisitLocationFields): boolean {
    return !isApproximateLocation(visit);
}

/**
 * "3.7 mi away", or "About 3.7 mi away" when measured to the intersection or a
 * rounded pin rather than the door.
 */
export function distanceAwayText(visit: VisitLocationFields & { distanceMiles: number }): string {
    return withholdsExactAddress(visit)
        ? `About ${visit.distanceMiles} mi away`
        : `${visit.distanceMiles} mi away`;
}

/**
 * The grid the API snaps an approximate pin to, in degrees. Must match
 * `APPROXIMATE_COORD_DECIMALS` (3) in packages/api/src/services/
 * visit-location-privacy.ts: 0.001 degrees is about 111 m north-south and
 * 84 m east-west in New York, so the patient is at most about 70 m from the pin.
 */
export const APPROXIMATE_COORD_STEP = 0.001;

/**
 * Static-map zoom for an approximate location. At zoom 16 (about 1.8 m per
 * pixel in New York) the ~84 m wide by 111 m tall square is about 46 x 61 px
 * in the 600 px image: big enough to read as "somewhere in here" around the
 * pin, with a few blocks of street context on every side. At the exact view's
 * zoom 15 the pin would cover the square; at 17 building footprints show and
 * the picture claims more precision than the pin has.
 */
export const APPROXIMATE_MAP_ZOOM = 16;

/**
 * Static-map zoom for a cross street: the pin is the intersection itself, so a
 * closer view (a couple of blocks each way) shows the two streets meeting and
 * their names.
 */
export const CROSS_STREET_MAP_ZOOM = 16;

/**
 * The square an approximate pin stands for: the patient is within half a grid
 * step of the pin in each direction. Returned as a closed ring for a Static
 * Maps `path`.
 */
export function approximateAreaRing(lat: number, lng: number): Array<[number, number]> {
    const half = APPROXIMATE_COORD_STEP / 2;
    const s = Math.round((lat - half) * 1e6) / 1e6;
    const n = Math.round((lat + half) * 1e6) / 1e6;
    const w = Math.round((lng - half) * 1e6) / 1e6;
    const e = Math.round((lng + half) * 1e6) / 1e6;
    return [
        [s, w],
        [s, e],
        [n, e],
        [n, w],
        [s, w]
    ];
}

/** "#45D2B0" (a theme token) → "0x45D2B0" for a Static Maps style param. */
export function staticMapColor(hex: string, alphaHex = ""): string {
    return `0x${hex.replace("#", "")}${alphaHex}`;
}

/**
 * Static map for an approximate location: the ~111 x 84 m square the patient
 * is somewhere inside, shaded, with a small pin at its centre so the pin does
 * not hide the square. Colours come from the caller (theme tokens), so this
 * file stays free of React Native imports and testable in Node.
 */
export function approximateStaticMapUrl(
    lat: number,
    lng: number,
    apiKey: string | undefined,
    palette: { fill: string; stroke: string }
): string {
    const ring = approximateAreaRing(lat, lng)
        .map(([a, b]) => `${a},${b}`)
        .join("%7C");
    const path = [
        `fillcolor:${staticMapColor(palette.fill, "33")}`,
        `color:${staticMapColor(palette.stroke, "CC")}`,
        "weight:2",
        ring
    ].join("%7C");
    return (
        "https://maps.googleapis.com/maps/api/staticmap" +
        `?center=${lat},${lng}&zoom=${APPROXIMATE_MAP_ZOOM}&size=600x360&scale=2&maptype=roadmap` +
        `&path=${path}` +
        `&markers=size:small%7Ccolor:${staticMapColor(palette.stroke)}%7C${lat},${lng}` +
        `&style=feature:poi%7Cvisibility:off&key=${apiKey ?? ""}`
    );
}

/**
 * Static map for a cross street: a pin on the intersection, in the theme's
 * colour. The patient's home is near the corner, not at the pin.
 */
export function crossStreetStaticMapUrl(
    lat: number,
    lng: number,
    apiKey: string | undefined,
    pinColor: string
): string {
    return (
        "https://maps.googleapis.com/maps/api/staticmap" +
        `?center=${lat},${lng}&zoom=${CROSS_STREET_MAP_ZOOM}&size=600x360&scale=2&maptype=roadmap` +
        `&markers=color:${staticMapColor(pinColor)}%7C${lat},${lng}` +
        `&style=feature:poi%7Cvisibility:off&key=${apiKey ?? ""}`
    );
}
